import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { Layout } from "@/components/layout";
import { Show, useUser } from "@clerk/react";
import {
  useGetCourse,
  useGetMyTeacherProfile,
  useUpdateMyCourse,
  useUpdateMyLesson,
  useCreateMyLesson,
  useDeleteMyLesson,
  useDeleteMyCourse,
  useStartLessonLive,
  useStopLessonLive,
  useCreateMyLessonReading,
  useUpdateMyReading,
  useDeleteMyReading,
  useCreateMyLessonHomework,
  useUpdateMyHomework,
  useDeleteMyHomework,
  useReorderMyCourseLessons,
  useReorderMyLessonReadings,
  useReorderMyLessonHomework,
  useCreateMyCourseSection,
  useUpdateMyCourseSection,
  useDeleteMyCourseSection,
  useUpsertMySectionLessonSession,
  getGetCourseQueryKey,
  getGetLessonQueryKey,
  getGetMyTeacherProfileQueryKey,
  getListMyTeacherCoursesQueryKey,
  useListMyLessonAttachments,
  getListMyLessonAttachmentsQueryKey,
  useCreateMyLessonAttachment,
  useDeleteMyLessonAttachment,
} from "@workspace/api-client-react";
import type {
  CourseSection,
  SectionSession,
  LessonWithRelations,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Radio, Square, Plus, Trash2, GripVertical, AlertTriangle, Upload, CheckCircle2, X, Eye, EyeOff, Globe, Paperclip } from "lucide-react";
import { useRequestUploadUrl, useRequestCoverUploadUrl } from "@workspace/api-client-react";
import { basePath } from "@/lib/clerk";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DraggableAttributes,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function dollarsFromCents(c: number) {
  return (c / 100).toFixed(2);
}

function useDragSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

type DragHandleProps = {
  attributes: DraggableAttributes;
  listeners: ReturnType<typeof useSortable>["listeners"];
};

function DragHandle({ attributes, listeners }: DragHandleProps) {
  return (
    <button
      type="button"
      aria-label="Drag to reorder"
      className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1 -ml-1"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
}

function CourseEditForm({ slug }: { slug: string }) {
  const { data: course } = useGetCourse(slug, {
    query: { enabled: !!slug, queryKey: getGetCourseQueryKey(slug) },
  });
  const updateCourse = useUpdateMyCourse();
  const updateLesson = useUpdateMyLesson();
  const reorderLessons = useReorderMyCourseLessons();
  const createLesson = useCreateMyLesson();
  const deleteLesson = useDeleteMyLesson();
  const qc = useQueryClient();
  const { toast } = useToast();
  const sensors = useDragSensors();
  const [newLessonTitle, setNewLessonTitle] = useState("");

  const [form, setForm] = useState({
    title: "",
    tagline: "",
    description: "",
    instructorName: "",
    instructorBio: "",
    priceDollars: "0.00",
    startDate: "",
    coverImageUrl: null as string | null,
  });

  // Local copy of lesson order so drag feels immediate; reset when server sends new data.
  const [lessonOrder, setLessonOrder] = useState<number[] | null>(null);
  useEffect(() => {
    if (course) setLessonOrder(course.lessons.map((l) => l.id));
  }, [course]);

  useEffect(() => {
    if (course) {
      setForm({
        title: course.title,
        tagline: course.tagline,
        description: course.description,
        instructorName: course.instructorName,
        instructorBio: course.instructorBio,
        priceDollars: dollarsFromCents(course.priceCents),
        startDate: course.startDate ? course.startDate.slice(0, 16) : "",
        coverImageUrl: course.coverImageUrl ?? null,
      });
    }
  }, [course]);

  const orderedLessons = useMemo(() => {
    if (!course) return [];
    if (!lessonOrder) return course.lessons;
    const byId = new Map(course.lessons.map((l) => [l.id, l]));
    return lessonOrder.map((id) => byId.get(id)).filter((l): l is NonNullable<typeof l> => !!l);
  }, [course, lessonOrder]);

  if (!course) {
    return (
      <div className="container mx-auto px-4 py-24 text-center text-muted-foreground" role="status">
        Loading course…
      </div>
    );
  }

  const onSaveCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    const priceCents = Math.round(parseFloat(form.priceDollars || "0") * 100);
    try {
      await updateCourse.mutateAsync({
        slug,
        data: {
          title: form.title,
          tagline: form.tagline,
          description: form.description,
          instructorName: form.instructorName,
          instructorBio: form.instructorBio,
          priceCents: Number.isFinite(priceCents) ? priceCents : 0,
          startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
          coverImageUrl: form.coverImageUrl,
        },
      });
      qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
      qc.invalidateQueries({ queryKey: getListMyTeacherCoursesQueryKey() });
      toast({ title: "Saved", description: "Course updated." });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const onLessonDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id || !lessonOrder) return;
    const oldIndex = lessonOrder.indexOf(Number(e.active.id));
    const newIndex = lessonOrder.indexOf(Number(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const previous = lessonOrder;
    const next = arrayMove(lessonOrder, oldIndex, newIndex);
    setLessonOrder(next);
    try {
      await reorderLessons.mutateAsync({ slug, data: { ids: next } });
      qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
    } catch (err) {
      setLessonOrder(previous);
      toast({
        title: "Couldn't reorder lessons",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16 max-w-4xl">
      <div className="mb-8">
        <Button asChild variant="ghost" size="sm" className="rounded-full -ml-2 mb-4">
          <Link href="/teacher">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to portal
          </Link>
        </Button>
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
          // editing course
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          {course.title}
        </h1>
      </div>

      <PublishPanel
        slug={slug}
        status={course.status}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
          qc.invalidateQueries({ queryKey: getListMyTeacherCoursesQueryKey() });
        }}
      />

      <form onSubmit={onSaveCourse} className="space-y-5 bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="tagline">Tagline</Label>
          <Input id="tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} required className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={6} className="mt-1.5" />
        </div>
        <div>
          <Label>Cover image</Label>
          <CourseCoverImageUploader
            coverImageUrl={form.coverImageUrl}
            onChange={(url) => setForm({ ...form, coverImageUrl: url })}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="price">Price (USD)</Label>
            <Input id="price" type="number" step="0.01" min="0" value={form.priceDollars} onChange={(e) => setForm({ ...form, priceDollars: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="start">Start date (optional)</Label>
            <Input id="start" type="datetime-local" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label htmlFor="instr">Instructor name</Label>
          <Input id="instr" value={form.instructorName} onChange={(e) => setForm({ ...form, instructorName: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="ibio">Instructor bio</Label>
          <Textarea id="ibio" value={form.instructorBio} onChange={(e) => setForm({ ...form, instructorBio: e.target.value })} rows={3} className="mt-1.5" />
        </div>
        <div className="pt-2">
          <Button type="submit" disabled={updateCourse.isPending} className="rounded-full">
            <Save className="w-4 h-4 mr-1.5" />
            {updateCourse.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      <div className="mt-12">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl font-bold">Lessons</h2>
          <p className="text-xs text-muted-foreground">Drag the handle to reorder.</p>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onLessonDragEnd}>
          <SortableContext items={orderedLessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-4">
              {orderedLessons.map((l, idx) => (
                <SortableLessonRow
                  key={l.id}
                  lesson={l}
                  positionLabel={idx + 1}
                  format={course.format}
                  courseSlug={slug}
                  onSave={async (patch) => {
                    await updateLesson.mutateAsync({ id: l.id, data: patch });
                    qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
                    toast({ title: "Lesson saved" });
                  }}
                  onDelete={async () => {
                    await deleteLesson.mutateAsync({ id: l.id });
                    qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
                    toast({ title: "Lesson removed" });
                  }}
                  onMaterialsChange={() => {
                    qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
                    qc.invalidateQueries({ queryKey: getGetLessonQueryKey(l.id) });
                  }}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const title = newLessonTitle.trim();
            if (!title) return;
            try {
              await createLesson.mutateAsync({ slug, data: { title } });
              setNewLessonTitle("");
              qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });
              toast({ title: "Lesson added" });
            } catch (err) {
              toast({
                title: "Couldn't add lesson",
                description: err instanceof Error ? err.message : "Try again.",
                variant: "destructive",
              });
            }
          }}
          className="mt-4 flex gap-2 items-end border border-dashed border-border rounded-xl p-4 bg-card/50"
        >
          <div className="flex-1">
            <Label htmlFor="new-lesson-title" className="text-xs">New lesson title</Label>
            <Input
              id="new-lesson-title"
              value={newLessonTitle}
              onChange={(e) => setNewLessonTitle(e.target.value)}
              placeholder="e.g. Functions and scope"
              className="mt-1.5"
            />
          </div>
          <Button
            type="submit"
            variant="outline"
            className="rounded-full"
            disabled={createLesson.isPending || !newLessonTitle.trim()}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {createLesson.isPending ? "Adding…" : "Add lesson"}
          </Button>
        </form>
      </div>

      {course.format === "live" && (
        <SectionsPanel
          slug={slug}
          sections={course.sections}
          lessons={course.lessons}
        />
      )}

      <DangerZone slug={slug} title={course.title} lessonCount={course.lessons.length} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections panel
// ---------------------------------------------------------------------------
// Lets a teacher run multiple cohorts of the same live course in parallel.
// Each section has its own start/end window, capacity, and per-lesson Zoom
// schedule. The student picks a section at checkout (course-detail.tsx) and
// /enrollments/me + the .ics feed both resolve zoom info from the picked
// section's lesson_sessions rows.
function SectionsPanel({
  slug,
  sections,
  lessons,
}: {
  slug: string;
  sections: CourseSection[];
  lessons: LessonWithRelations[];
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const createSection = useCreateMyCourseSection();
  const [newLabel, setNewLabel] = useState("");
  const [newCapacity, setNewCapacity] = useState("20");
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: getGetCourseQueryKey(slug) });

  return (
    <div className="border rounded-2xl p-6 bg-card space-y-6">
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight">
          Cohort sections
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Run multiple cohorts of this course in parallel — each section has
          its own schedule, capacity, and Zoom links. Students pick a section
          at checkout.
        </p>
      </div>

      <div className="space-y-4">
        {sections.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            No sections yet — add one below.
          </p>
        )}
        {sections.map((s) => (
          <SectionRow
            key={s.id}
            slug={slug}
            section={s}
            lessons={lessons}
            onChanged={invalidate}
          />
        ))}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const label = newLabel.trim();
          const cap = Number(newCapacity);
          if (!label) return;
          if (!Number.isFinite(cap) || cap < 1) {
            toast({
              title: "Invalid capacity",
              description: "Capacity must be at least 1.",
              variant: "destructive",
            });
            return;
          }
          try {
            await createSection.mutateAsync({
              slug,
              data: { label, capacity: cap, status: "published" },
            });
            setNewLabel("");
            setNewCapacity("20");
            invalidate();
            toast({ title: "Section added" });
          } catch (err: any) {
            toast({
              title: "Couldn't add section",
              description: err?.data?.error || "Try again.",
              variant: "destructive",
            });
          }
        }}
        className="flex gap-2 items-end border border-dashed border-border rounded-xl p-4"
      >
        <div className="flex-1">
          <Label className="text-xs">New section label</Label>
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="e.g. Fall 2026 — Tue/Thu 7pm ET"
            className="mt-1.5"
          />
        </div>
        <div className="w-28">
          <Label className="text-xs">Capacity</Label>
          <Input
            type="number"
            min={1}
            value={newCapacity}
            onChange={(e) => setNewCapacity(e.target.value)}
            className="mt-1.5"
          />
        </div>
        <Button
          type="submit"
          variant="outline"
          className="rounded-full"
          disabled={createSection.isPending || !newLabel.trim()}
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {createSection.isPending ? "Adding…" : "Add section"}
        </Button>
      </form>
    </div>
  );
}

function dateInputValue(iso: string | null): string {
  if (!iso) return "";
  // <input type=date> needs YYYY-MM-DD in the user's local timezone.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function datetimeInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SectionRow({
  slug,
  section,
  lessons,
  onChanged,
}: {
  slug: string;
  section: CourseSection;
  lessons: LessonWithRelations[];
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const updateSection = useUpdateMyCourseSection();
  const deleteSection = useDeleteMyCourseSection();
  const [draft, setDraft] = useState({
    label: section.label,
    description: section.description ?? "",
    startDate: dateInputValue(section.startDate),
    endDate: dateInputValue(section.endDate),
    capacity: String(section.capacity),
    status: section.status,
  });
  const [expanded, setExpanded] = useState(false);

  // Re-seed local draft whenever the server-side section changes (e.g. after
  // saving capacity, or a sibling teacher edit). Without this, the form would
  // keep showing stale values after a successful save.
  useEffect(() => {
    setDraft({
      label: section.label,
      description: section.description ?? "",
      startDate: dateInputValue(section.startDate),
      endDate: dateInputValue(section.endDate),
      capacity: String(section.capacity),
      status: section.status,
    });
  }, [section]);

  const dirty =
    draft.label !== section.label ||
    draft.description !== (section.description ?? "") ||
    draft.startDate !== dateInputValue(section.startDate) ||
    draft.endDate !== dateInputValue(section.endDate) ||
    draft.capacity !== String(section.capacity) ||
    draft.status !== section.status;

  return (
    <div className="border rounded-xl p-4 bg-background/40 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-bold">{section.label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {section.seatsRemaining} / {section.capacity} seats left ·{" "}
            <span className="uppercase tracking-wider">{section.status}</span>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full h-8"
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded ? "Hide schedule" : "Edit schedule"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-full h-8 text-destructive hover:text-destructive"
            disabled={deleteSection.isPending}
            onClick={async () => {
              if (!confirm(`Delete section "${section.label}"?`)) return;
              try {
                await deleteSection.mutateAsync({
                  slug,
                  sectionId: section.id,
                });
                onChanged();
                toast({ title: "Section deleted" });
              } catch (err: any) {
                toast({
                  title: "Couldn't delete section",
                  description: err?.data?.error || "Try again.",
                  variant: "destructive",
                });
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const cap = Number(draft.capacity);
          if (!Number.isFinite(cap) || cap < 1) {
            toast({
              title: "Invalid capacity",
              variant: "destructive",
            });
            return;
          }
          try {
            await updateSection.mutateAsync({
              slug,
              sectionId: section.id,
              data: {
                label: draft.label,
                description: draft.description || null,
                startDate: draft.startDate
                  ? new Date(draft.startDate).toISOString()
                  : null,
                endDate: draft.endDate
                  ? new Date(draft.endDate).toISOString()
                  : null,
                capacity: cap,
                status: draft.status,
              },
            });
            onChanged();
            toast({ title: "Section saved" });
          } catch (err: any) {
            toast({
              title: "Couldn't save section",
              description: err?.data?.error || "Try again.",
              variant: "destructive",
            });
          }
        }}
        className="grid grid-cols-1 md:grid-cols-2 gap-3"
      >
        <div className="md:col-span-2">
          <Label className="text-xs">Label</Label>
          <Input
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Start date</Label>
          <Input
            type="date"
            value={draft.startDate}
            onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">End date</Label>
          <Input
            type="date"
            value={draft.endDate}
            onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Capacity</Label>
          <Input
            type="number"
            min={1}
            value={draft.capacity}
            onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select
            value={draft.status}
            onChange={(e) =>
              setDraft({ ...draft, status: e.target.value as typeof draft.status })
            }
            className="mt-1 h-9 w-full border rounded-md bg-background px-2 text-sm"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="unpublished">Unpublished</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Description (optional)</Label>
          <Textarea
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
            rows={2}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button
            type="submit"
            size="sm"
            variant="outline"
            className="rounded-full h-9"
            disabled={!dirty || updateSection.isPending}
          >
            <Save className="w-4 h-4 mr-1" />
            {updateSection.isPending ? "Saving…" : "Save section"}
          </Button>
        </div>
      </form>

      {expanded && (
        <div className="pt-3 mt-1 border-t border-dashed space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Per-lesson Zoom schedule
          </div>
          {lessons.map((lesson) => {
            const session = section.sessions.find(
              (s) => s.lessonId === lesson.id,
            );
            return (
              <LessonSessionRow
                key={lesson.id}
                sectionId={section.id}
                lesson={lesson}
                session={session}
                onChanged={onChanged}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function LessonSessionRow({
  sectionId,
  lesson,
  session,
  onChanged,
}: {
  sectionId: number;
  lesson: LessonWithRelations;
  session: SectionSession | undefined;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const upsert = useUpsertMySectionLessonSession();
  const [zoomUrl, setZoomUrl] = useState(session?.zoomUrl ?? "");
  const [zoomScheduledAt, setZoomScheduledAt] = useState(
    datetimeInputValue(session?.zoomScheduledAt ?? null),
  );

  // Sync local form when the server-side row changes (e.g. teacher edits in
  // two tabs). Without this the inputs would keep stale values after save.
  useEffect(() => {
    setZoomUrl(session?.zoomUrl ?? "");
    setZoomScheduledAt(datetimeInputValue(session?.zoomScheduledAt ?? null));
  }, [session]);

  const dirty =
    zoomUrl !== (session?.zoomUrl ?? "") ||
    zoomScheduledAt !== datetimeInputValue(session?.zoomScheduledAt ?? null);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await upsert.mutateAsync({
            sectionId,
            lessonId: lesson.id,
            data: {
              zoomUrl: zoomUrl.trim() || null,
              zoomScheduledAt: zoomScheduledAt
                ? new Date(zoomScheduledAt).toISOString()
                : null,
            },
          });
          onChanged();
          toast({ title: "Saved" });
        } catch (err: any) {
          toast({
            title: "Couldn't save lesson schedule",
            description: err?.data?.error || "Try again.",
            variant: "destructive",
          });
        }
      }}
      className="grid grid-cols-12 gap-2 items-end"
    >
      <div className="col-span-12 md:col-span-3 text-xs font-medium truncate">
        Lesson {lesson.orderIndex}: {lesson.title}
      </div>
      <div className="col-span-6 md:col-span-4">
        <Input
          type="datetime-local"
          value={zoomScheduledAt}
          onChange={(e) => setZoomScheduledAt(e.target.value)}
          className="h-9"
        />
      </div>
      <div className="col-span-6 md:col-span-4">
        <Input
          value={zoomUrl}
          onChange={(e) => setZoomUrl(e.target.value)}
          placeholder="Zoom URL"
          className="h-9"
        />
      </div>
      <div className="col-span-12 md:col-span-1 flex justify-end">
        <Button
          type="submit"
          size="sm"
          variant="outline"
          className="rounded-full h-9"
          disabled={!dirty || upsert.isPending}
        >
          <Save className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
}

function PublishPanel({
  slug,
  status,
  onChanged,
}: {
  slug: string;
  status: "draft" | "published" | "unpublished" | string;
  onChanged: () => void;
}) {
  const updateCourse = useUpdateMyCourse();
  const { toast } = useToast();

  const setStatus = async (
    next: "draft" | "published" | "unpublished",
    label: string,
  ) => {
    try {
      await updateCourse.mutateAsync({ slug, data: { status: next } });
      onChanged();
      toast({ title: label });
    } catch (err) {
      toast({
        title: "Couldn't change visibility",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const isPublished = status === "published";
  const isDraft = status === "draft";
  const isUnpublished = status === "unpublished";

  return (
    <div
      className={`mb-6 rounded-2xl border p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${
        isPublished
          ? "border-primary/30 bg-primary/[0.04]"
          : isDraft
            ? "border-amber-500/40 bg-amber-50/60"
            : "border-border bg-muted/30"
      }`}
      data-testid="panel-publish-status"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {isPublished ? (
            <Globe className="w-4 h-4 text-primary" aria-hidden="true" />
          ) : isDraft ? (
            <Eye className="w-4 h-4 text-amber-700" aria-hidden="true" />
          ) : (
            <EyeOff className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          )}
          <Badge
            variant="outline"
            className={`rounded-full text-[10px] uppercase tracking-wider ${
              isPublished
                ? "border-primary/40 text-primary"
                : isDraft
                  ? "border-amber-500/50 text-amber-700 bg-amber-50"
                  : "text-muted-foreground"
            }`}
            data-testid="badge-status"
          >
            {isPublished ? "Published" : isDraft ? "Draft" : "Unpublished"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-xl">
          {isPublished
            ? "This course is live on the catalog and open for enrollment."
            : isDraft
              ? "Drafts are hidden from students. Publish when you're ready to take enrollments."
              : "Hidden from the catalog. Already-enrolled students keep full access — re-publish to start taking new enrollments again."}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {!isPublished && (
          <Button
            type="button"
            className="rounded-full"
            disabled={updateCourse.isPending}
            onClick={() => setStatus("published", "Course published")}
            data-testid="button-publish"
          >
            <Globe className="w-4 h-4 mr-1.5" /> Publish
          </Button>
        )}
        {isPublished && (
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            disabled={updateCourse.isPending}
            onClick={() => setStatus("unpublished", "Course unpublished")}
            data-testid="button-unpublish"
          >
            <EyeOff className="w-4 h-4 mr-1.5" /> Unpublish
          </Button>
        )}
        {isUnpublished && (
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            disabled={updateCourse.isPending}
            onClick={() => setStatus("draft", "Moved back to draft")}
            data-testid="button-back-to-draft"
          >
            Move to draft
          </Button>
        )}
      </div>
    </div>
  );
}

function DangerZone({
  slug,
  title,
  lessonCount,
}: {
  slug: string;
  title: string;
  lessonCount: number;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [, navigate] = useLocation();
  const deleteCourse = useDeleteMyCourse();
  const qc = useQueryClient();
  const { toast } = useToast();

  const matches = confirmText.trim() === slug;

  const onDelete = async () => {
    if (!matches) return;
    try {
      await deleteCourse.mutateAsync({ slug });
      // Drop the cached course entirely — invalidating would just trigger a
      // 404 refetch as we navigate away.
      qc.removeQueries({ queryKey: getGetCourseQueryKey(slug) });
      qc.invalidateQueries({ queryKey: getListMyTeacherCoursesQueryKey() });
      toast({
        title: "Course deleted",
        description: `"${title}" and all of its lessons, enrollments, and messages were removed.`,
      });
      navigate("/teacher");
    } catch (err) {
      toast({
        title: "Couldn't delete course",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-16 border border-destructive/40 rounded-2xl p-6 md:p-8 bg-destructive/[0.04]">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="font-display text-2xl font-bold text-foreground">Danger zone</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently delete this course. This removes all{" "}
            <strong className="text-foreground">{lessonCount}</strong>{" "}
            {lessonCount === 1 ? "lesson" : "lessons"}, every student
            enrollment, and every broadcast message tied to it. Saved student
            code will lose its lesson link but will not be deleted. This cannot
            be undone.
          </p>
        </div>
      </div>
      <div className="mt-5 max-w-md">
        <Label htmlFor="dz-confirm" className="text-xs">
          Type <code className="px-1 py-0.5 rounded bg-muted text-foreground font-mono">{slug}</code> to confirm
        </Label>
        <Input
          id="dz-confirm"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={slug}
          autoComplete="off"
          className="mt-1.5 font-mono text-sm"
          data-testid="input-confirm-delete-course"
        />
      </div>
      <div className="mt-4">
        <Button
          type="button"
          variant="destructive"
          className="rounded-full"
          disabled={!matches || deleteCourse.isPending}
          onClick={onDelete}
          data-testid="button-delete-course"
        >
          <Trash2 className="w-4 h-4 mr-1.5" />
          {deleteCourse.isPending ? "Deleting…" : "Delete course permanently"}
        </Button>
      </div>
    </div>
  );
}

type SandboxLanguage = "python" | "web" | null;

type LessonPatch = {
  title?: string;
  summary?: string;
  zoomUrl?: string | null;
  zoomScheduledAt?: string | null;
  zoomLiveStartedAt?: string | null;
  videoObjectPath?: string | null;
  videoDurationMinutes?: number | null;
  sandboxLanguage?: SandboxLanguage;
  sandboxStarter?: string | null;
};

type ReadingItem = {
  id: number;
  lessonId: number;
  orderIndex: number;
  title: string;
  url: string | null;
  estimatedMinutes: number;
};

type HomeworkItem = {
  id: number;
  lessonId: number;
  orderIndex: number;
  title: string;
  instructions: string;
  estimatedMinutes: number;
};

type LessonForRow = {
  id: number;
  orderIndex: number;
  title: string;
  summary: string;
  zoomUrl: string | null;
  zoomScheduledAt: string | null;
  zoomLiveStartedAt: string | null;
  hasVideo?: boolean;
  videoDurationMinutes: number | null;
  sandboxLanguage: string | null;
  sandboxStarter: string | null;
  readings: ReadingItem[];
  homework: HomeworkItem[];
};

function LessonVideoUploader({
  lessonId,
  hasExistingVideo,
  pendingChange,
  onChange,
}: {
  lessonId: number;
  hasExistingVideo: boolean;
  pendingChange: string | null | undefined;
  onChange: (objectPath: string | null) => void;
}) {
  // Resolve the visible state without ever exposing the actual storage key:
  // - pendingChange === undefined → no edit yet, mirror what the server has.
  // - pendingChange === null      → teacher cleared the existing video.
  // - pendingChange === string    → teacher just uploaded a new video.
  const status: "uploaded" | "attached" | "cleared" | "empty" =
    typeof pendingChange === "string"
      ? "uploaded"
      : pendingChange === null
        ? "cleared"
        : hasExistingVideo
          ? "attached"
          : "empty";
  const { toast } = useToast();
  const requestUrl = useRequestUploadUrl();
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (file: File) => {
    if (!file.type.startsWith("video/")) {
      toast({
        title: "Not a video file",
        description: "Pick an mp4, webm, or mov file.",
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { uploadURL, objectPath } = await requestUrl.mutateAsync({
        data: { name: file.name, size: file.size, contentType: file.type },
      });

      // GCS resumable URLs accept a single PUT with the whole body. Use XHR
      // so we can show real upload progress for the (potentially large)
      // video file without buffering it through our API.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadURL);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(file);
      });

      onChange(objectPath);
      toast({
        title: "Video uploaded",
        description: "Click Save lesson to attach it.",
      });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <div className="mt-1.5 space-y-2">
      {status === "uploaded" || status === "attached" ? (
        <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border bg-muted/40">
          <div className="flex items-center gap-2 min-w-0 text-sm">
            <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
            <span className="truncate">
              {status === "uploaded"
                ? "New video ready — Save lesson to attach it."
                : "Video attached. Replace or remove it below."}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            disabled={uploading}
            aria-label="Remove video"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : status === "cleared" ? (
        <p className="text-xs text-amber-600">
          Video will be removed when you save this lesson.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Upload an mp4/webm/mov. Only enrolled students will be able to play it.
        </p>
      )}
      <div className="flex items-center gap-2">
        <input
          id={`l-vfile-${lessonId}`}
          type="file"
          accept="video/*"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          asChild
          disabled={uploading}
        >
          <label htmlFor={`l-vfile-${lessonId}`} className="cursor-pointer">
            <Upload className="w-4 h-4 mr-1.5" />
            {uploading
              ? progress !== null
                ? `Uploading ${progress}%`
                : "Uploading…"
              : status === "uploaded" || status === "attached"
              ? "Replace video"
              : "Upload video"}
          </label>
        </Button>
      </div>
    </div>
  );
}

function CourseCoverImageUploader({
  coverImageUrl,
  onChange,
}: {
  coverImageUrl: string | null;
  onChange: (url: string | null) => void;
}) {
  const { toast } = useToast();
  const requestUrl = useRequestCoverUploadUrl();
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Not an image file",
        description: "Pick a jpg, png, webp, or gif file.",
        variant: "destructive",
      });
      return;
    }
    // 10 MB ceiling — cover images don't need to be huge, and bigger files
    // just make landing pages slower.
    const MAX_BYTES = 10 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast({
        title: "Image too large",
        description: "Cover images must be under 10 MB.",
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { uploadURL, objectPath } = await requestUrl.mutateAsync({
        data: { name: file.name, size: file.size, contentType: file.type },
      });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadURL);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(file);
      });

      // objectPath comes back as `/objects/covers/<uuid>`. We expose covers
      // publicly through `/api/objects/covers/<uuid>`, so store the proxy URL
      // directly — that way `<img src={coverImageUrl}>` just works on every
      // public surface.
      const publicUrl = objectPath.startsWith("/objects/")
        ? `/api${objectPath}`
        : objectPath;
      onChange(publicUrl);
      toast({
        title: "Cover image uploaded",
        description: "Click Save changes to attach it.",
      });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <div className="mt-1.5 space-y-2">
      {coverImageUrl ? (
        <div className="flex items-start gap-3">
          <img
            src={coverImageUrl}
            alt="Course cover"
            className="w-40 h-24 object-cover rounded-md border bg-muted"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            disabled={uploading}
          >
            <X className="w-4 h-4 mr-1.5" /> Remove
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Shows on the landing page, course catalog, and the course detail page.
        </p>
      )}
      <div className="flex items-center gap-2">
        <input
          id="course-cover-file"
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          asChild
          disabled={uploading}
        >
          <label htmlFor="course-cover-file" className="cursor-pointer">
            <Upload className="w-4 h-4 mr-1.5" />
            {uploading
              ? progress !== null
                ? `Uploading ${progress}%`
                : "Uploading…"
              : coverImageUrl
                ? "Replace image"
                : "Upload image"}
          </label>
        </Button>
      </div>
    </div>
  );
}

function SortableLessonRow(props: {
  lesson: LessonForRow;
  positionLabel: number;
  format: string;
  onSave: (patch: LessonPatch) => Promise<void>;
  onDelete: () => Promise<void>;
  courseSlug: string;
  onMaterialsChange: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.lesson.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <li ref={setNodeRef} style={style} className="border border-border rounded-xl bg-card overflow-hidden">
      <LessonRowBody
        {...props}
        dragHandle={<DragHandle attributes={attributes} listeners={listeners} />}
      />
    </li>
  );
}

function LessonRowBody({
  lesson,
  positionLabel,
  format,
  onSave,
  onDelete,
  courseSlug,
  onMaterialsChange,
  dragHandle,
}: {
  lesson: LessonForRow;
  positionLabel: number;
  format: string;
  onSave: (patch: LessonPatch) => Promise<void>;
  onDelete: () => Promise<void>;
  courseSlug: string;
  onMaterialsChange: () => void;
  dragHandle: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const qc = useQueryClient();
  const { toast } = useToast();
  const startLive = useStartLessonLive();
  const stopLive = useStopLessonLive();
  const onDeleteClick = async () => {
    const materialsCount = lesson.readings.length + lesson.homework.length;
    const extra = materialsCount
      ? ` This will also remove ${materialsCount} reading${lesson.readings.length === 1 ? "" : "s"}/homework item${materialsCount === 1 ? "" : "s"}.`
      : "";
    if (!window.confirm(`Delete lesson "${lesson.title}"?${extra} This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      toast({
        title: "Couldn't delete lesson",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };
  const isLiveNow = !!lesson.zoomLiveStartedAt;
  const canStart = format === "live" && !!lesson.zoomUrl;
  const toggleLive = async () => {
    try {
      if (isLiveNow) {
        await stopLive.mutateAsync({ id: lesson.id });
        toast({ title: "Session ended" });
      } else {
        await startLive.mutateAsync({ id: lesson.id });
        toast({
          title: "Session started",
          description: "Enrolled students can now join from their lesson page.",
        });
      }
      qc.invalidateQueries({ queryKey: getGetCourseQueryKey(courseSlug) });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not update session";
      toast({ title: "Couldn't update session", description: message, variant: "destructive" });
    }
  };
  const [draft, setDraft] = useState({
    title: lesson.title,
    summary: lesson.summary,
    zoomUrl: lesson.zoomUrl ?? "",
    zoomScheduledAt: lesson.zoomScheduledAt ? lesson.zoomScheduledAt.slice(0, 16) : "",
    // `undefined` = unchanged from server. `null` = teacher cleared the
    // video. A string = teacher uploaded a new one. We never receive the
    // current path from /courses/:slug (it's stripped server-side), so we
    // can only act when the teacher explicitly changes it.
    videoObjectPath: undefined as string | null | undefined,
    videoDurationMinutes: lesson.videoDurationMinutes ?? 0,
    sandboxLanguage: (lesson.sandboxLanguage as SandboxLanguage) ?? null,
    sandboxStarter: lesson.sandboxStarter ?? "",
  });
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch: LessonPatch = {
        title: draft.title,
        summary: draft.summary,
        sandboxLanguage: draft.sandboxLanguage,
        sandboxStarter: draft.sandboxLanguage ? draft.sandboxStarter : null,
      };
      if (format === "live") {
        patch.zoomUrl = draft.zoomUrl || null;
        patch.zoomScheduledAt = draft.zoomScheduledAt
          ? new Date(draft.zoomScheduledAt).toISOString()
          : null;
      } else {
        if (draft.videoObjectPath !== undefined) {
          patch.videoObjectPath = draft.videoObjectPath;
        }
        patch.videoDurationMinutes = draft.videoDurationMinutes
          ? Number(draft.videoDurationMinutes)
          : null;
      }
      await onSave(patch);
      setOpen(false);
    } catch (err) {
      toast({
        title: "Couldn't save lesson",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {dragHandle}
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <span>Lesson {positionLabel}</span>
              {isLiveNow && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground border border-secondary/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
                  Live now
                </span>
              )}
            </div>
            <div className="font-medium truncate">{lesson.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {format === "live" && (
            isLiveNow ? (
              <Button
                size="sm"
                variant="destructive"
                className="rounded-full"
                onClick={toggleLive}
                disabled={stopLive.isPending}
              >
                <Square className="w-3.5 h-3.5 mr-1.5 fill-current" />
                {stopLive.isPending ? "Ending…" : "End session"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="default"
                className="rounded-full"
                onClick={toggleLive}
                disabled={!canStart || startLive.isPending}
                title={canStart ? "Mark this Zoom session as live" : "Add a Zoom URL first"}
              >
                <Radio className="w-3.5 h-3.5 mr-1.5" />
                {startLive.isPending ? "Starting…" : "Start session"}
              </Button>
            )
          )}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen((o) => !o)}>
            {open ? "Cancel" : "Edit"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full text-destructive hover:text-destructive"
            onClick={onDeleteClick}
            disabled={deleting || isLiveNow}
            title={isLiveNow ? "End the live session before deleting" : "Delete lesson"}
            aria-label="Delete lesson"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {open && (
        <form onSubmit={onSubmit} className="px-5 pb-5 pt-1 border-t border-border space-y-3 bg-background/50">
          <div>
            <Label htmlFor={`l-title-${lesson.id}`}>Title</Label>
            <Input id={`l-title-${lesson.id}`} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor={`l-sum-${lesson.id}`}>Summary</Label>
            <Textarea id={`l-sum-${lesson.id}`} value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={3} className="mt-1.5" />
          </div>
          {format === "live" ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`l-zurl-${lesson.id}`}>Zoom URL</Label>
                <Input id={`l-zurl-${lesson.id}`} value={draft.zoomUrl} onChange={(e) => setDraft({ ...draft, zoomUrl: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor={`l-zat-${lesson.id}`}>Scheduled at</Label>
                <Input id={`l-zat-${lesson.id}`} type="datetime-local" value={draft.zoomScheduledAt} onChange={(e) => setDraft({ ...draft, zoomScheduledAt: e.target.value })} className="mt-1.5" />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Lesson video</Label>
                <LessonVideoUploader
                  lessonId={lesson.id}
                  hasExistingVideo={!!lesson.hasVideo}
                  pendingChange={draft.videoObjectPath}
                  onChange={(objectPath) => setDraft({ ...draft, videoObjectPath: objectPath })}
                />
              </div>
              <div>
                <Label htmlFor={`l-vdur-${lesson.id}`}>Duration (minutes)</Label>
                <Input id={`l-vdur-${lesson.id}`} type="number" min="0" value={draft.videoDurationMinutes} onChange={(e) => setDraft({ ...draft, videoDurationMinutes: Number(e.target.value) })} className="mt-1.5" />
              </div>
            </div>
          )}
          <SandboxFields
            lessonId={lesson.id}
            value={{ language: draft.sandboxLanguage, starter: draft.sandboxStarter }}
            onChange={(v) =>
              setDraft({ ...draft, sandboxLanguage: v.language, sandboxStarter: v.starter })
            }
          />
          <Button type="submit" size="sm" className="rounded-full" disabled={saving}>
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save lesson"}
          </Button>
        </form>
      )}
      {open && (
        <div className="px-5 pb-5 pt-4 border-t border-border bg-background/50 space-y-6">
          <ReadingsEditor
            lessonId={lesson.id}
            readings={lesson.readings}
            onChange={onMaterialsChange}
          />
          <HomeworkEditor
            lessonId={lesson.id}
            homework={lesson.homework}
            onChange={onMaterialsChange}
          />
          <AttachmentsEditor lessonId={lesson.id} />
        </div>
      )}
    </>
  );
}

function AttachmentsEditor({ lessonId }: { lessonId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: attachments } = useListMyLessonAttachments(lessonId, {
    query: { queryKey: getListMyLessonAttachmentsQueryKey(lessonId) },
  });
  const requestUrl = useRequestUploadUrl();
  const createAttachment = useCreateMyLessonAttachment();
  const deleteAttachment = useDeleteMyLessonAttachment();
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: getListMyLessonAttachmentsQueryKey(lessonId),
    });
    void queryClient.invalidateQueries({
      queryKey: getGetLessonQueryKey(lessonId),
    });
  };

  const onPick = async (file: File) => {
    const MAX_BYTES = 50 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast({
        title: "File too large",
        description: "Attachments must be under 50 MB.",
        variant: "destructive",
      });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const contentType = file.type || "application/octet-stream";
      const { uploadURL, objectPath } = await requestUrl.mutateAsync({
        data: { name: file.name, size: file.size, contentType },
      });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadURL);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Network error during upload"));
        xhr.send(file);
      });

      await createAttachment.mutateAsync({
        id: lessonId,
        data: {
          title: file.name.replace(/\.[^.]+$/, ""),
          fileName: file.name,
          fileSizeBytes: file.size,
          contentType,
          objectPath,
        },
      });
      invalidate();
      toast({
        title: "Attachment added",
        description: "Students can download it from the lesson page.",
      });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const onDelete = async (attachmentId: number, title: string) => {
    if (!window.confirm(`Remove attachment "${title}"?`)) return;
    try {
      await deleteAttachment.mutateAsync({ id: attachmentId });
      invalidate();
      toast({ title: "Attachment removed" });
    } catch {
      toast({
        title: "Couldn't remove attachment",
        description: "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <h3 className="font-display text-base font-semibold mb-2 flex items-center gap-1.5">
        <Paperclip className="w-4 h-4 text-muted-foreground" /> Downloadable files
      </h3>
      <ul className="space-y-2 mb-3">
        {(attachments ?? []).map((att) => (
          <li
            key={att.id}
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border bg-muted/40 text-sm"
          >
            <div className="min-w-0">
              <div className="font-medium truncate">{att.title}</div>
              <div className="text-xs text-muted-foreground truncate">
                {att.fileName} · {Math.max(1, Math.round(att.fileSizeBytes / 1024))} KB
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`Remove ${att.title}`}
              onClick={() => void onDelete(att.id, att.title)}
              disabled={deleteAttachment.isPending}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </li>
        ))}
        {(attachments ?? []).length === 0 && (
          <li className="text-sm text-muted-foreground italic">No files yet.</li>
        )}
      </ul>
      <input
        id={`l-attfile-${lessonId}`}
        type="file"
        className="sr-only"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPick(f);
          e.target.value = "";
        }}
      />
      <Button type="button" size="sm" variant="outline" asChild disabled={uploading}>
        <label htmlFor={`l-attfile-${lessonId}`} className="cursor-pointer">
          <Upload className="w-4 h-4 mr-1.5" />
          {uploading
            ? progress !== null
              ? `Uploading ${progress}%`
              : "Uploading…"
            : "Upload file"}
        </label>
      </Button>
      <p className="text-[11px] text-muted-foreground mt-2">
        Worksheets, slides, starter files — students enrolled in this course can download them.
      </p>
    </div>
  );
}

function SandboxFields({
  lessonId,
  value,
  onChange,
}: {
  lessonId: number;
  value: { language: SandboxLanguage; starter: string };
  onChange: (v: { language: SandboxLanguage; starter: string }) => void;
}) {
  const placeholderByLang: Record<"python" | "web", string> = {
    python: `# Python starter\nname = "world"\nprint(f"Hello, {name}!")\n`,
    web: `{\n  "/index.html": "<!doctype html>\\n<html><body><h1>Hi</h1></body></html>",\n  "/styles.css": "body { font-family: sans-serif; }"\n}`,
  };
  return (
    <div className="border border-dashed border-border rounded-lg p-3 space-y-2 bg-background/40">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            // in-lesson code sandbox
          </div>
          <Label htmlFor={`l-sblang-${lessonId}`} className="text-sm">
            Default starter code
          </Label>
        </div>
        <select
          id={`l-sblang-${lessonId}`}
          value={value.language ?? ""}
          onChange={(e) => {
            const next = e.target.value as "" | "python" | "web";
            onChange({
              language: next === "" ? null : next,
              starter: value.starter,
            });
          }}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Off</option>
          <option value="python">Python (Pyodide)</option>
          <option value="web">Web (HTML/CSS/JS)</option>
        </select>
      </div>
      {value.language && (
        <>
          <Textarea
            id={`l-sbstart-${lessonId}`}
            value={value.starter}
            onChange={(e) => onChange({ language: value.language, starter: e.target.value })}
            rows={8}
            spellCheck={false}
            className="mt-1 font-mono text-xs leading-relaxed"
            placeholder={placeholderByLang[value.language]}
          />
          <p className="text-[11px] text-muted-foreground">
            {value.language === "python"
              ? "Plain Python source. Students see this when they open the lesson."
              : "JSON map of file paths → contents (e.g. /index.html, /styles.css, /script.js)."}
          </p>
        </>
      )}
    </div>
  );
}

function ReadingsEditor({
  lessonId,
  readings,
  onChange,
}: {
  lessonId: number;
  readings: ReadingItem[];
  onChange: () => void;
}) {
  const create = useCreateMyLessonReading();
  const update = useUpdateMyReading();
  const remove = useDeleteMyReading();
  const reorder = useReorderMyLessonReadings();
  const { toast } = useToast();
  const sensors = useDragSensors();
  const [draft, setDraft] = useState({ title: "", url: "", estimatedMinutes: "10" });
  const [order, setOrder] = useState<number[]>(readings.map((r) => r.id));

  useEffect(() => {
    setOrder(readings.map((r) => r.id));
  }, [readings]);

  const ordered = useMemo(() => {
    const byId = new Map(readings.map((r) => [r.id, r]));
    return order.map((id) => byId.get(id)).filter((r): r is ReadingItem => !!r);
  }, [order, readings]);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    const orderIndex = readings.length
      ? Math.max(...readings.map((r) => r.orderIndex)) + 1
      : 0;
    try {
      await create.mutateAsync({
        id: lessonId,
        data: {
          orderIndex,
          title: draft.title.trim(),
          url: draft.url.trim() || null,
          estimatedMinutes: Number(draft.estimatedMinutes) || 0,
        },
      });
      setDraft({ title: "", url: "", estimatedMinutes: "10" });
      onChange();
      toast({ title: "Reading added" });
    } catch (err) {
      toast({
        title: "Couldn't add reading",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIndex = order.indexOf(Number(e.active.id));
    const newIndex = order.indexOf(Number(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const previous = order;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    try {
      await reorder.mutateAsync({ id: lessonId, data: { ids: next } });
      onChange();
    } catch (err) {
      setOrder(previous);
      toast({
        title: "Couldn't reorder readings",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section>
      <h3 className="font-display text-base font-semibold mb-2">Readings</h3>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ordered.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-2">
            {ordered.map((r) => (
              <SortableReadingRow
                key={r.id}
                reading={r}
                onSave={async (patch) => {
                  try {
                    await update.mutateAsync({ id: r.id, data: patch });
                    onChange();
                    toast({ title: "Reading saved" });
                  } catch (err) {
                    toast({
                      title: "Couldn't save reading",
                      description: err instanceof Error ? err.message : "Try again.",
                      variant: "destructive",
                    });
                    throw err;
                  }
                }}
                onDelete={async () => {
                  try {
                    await remove.mutateAsync({ id: r.id });
                    onChange();
                    toast({ title: "Reading removed" });
                  } catch (err) {
                    toast({
                      title: "Couldn't remove reading",
                      description: err instanceof Error ? err.message : "Try again.",
                      variant: "destructive",
                    });
                    throw err;
                  }
                }}
              />
            ))}
            {ordered.length === 0 && (
              <li className="text-sm text-muted-foreground italic">No readings yet.</li>
            )}
          </ul>
        </SortableContext>
      </DndContext>
      <form onSubmit={onAdd} className="mt-3 grid sm:grid-cols-[1fr_1fr_100px_auto] gap-2 items-end">
        <div>
          <Label htmlFor={`new-r-title-${lessonId}`} className="text-xs">Title</Label>
          <Input
            id={`new-r-title-${lessonId}`}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Reading title"
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label htmlFor={`new-r-url-${lessonId}`} className="text-xs">URL (optional)</Label>
          <Input
            id={`new-r-url-${lessonId}`}
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            placeholder="https://…"
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label htmlFor={`new-r-min-${lessonId}`} className="text-xs">Minutes</Label>
          <Input
            id={`new-r-min-${lessonId}`}
            type="number"
            min="0"
            value={draft.estimatedMinutes}
            onChange={(e) => setDraft({ ...draft, estimatedMinutes: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <Button type="submit" size="sm" variant="outline" className="rounded-full h-9" disabled={create.isPending}>
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </form>
    </section>
  );
}

function SortableReadingRow(props: {
  reading: ReadingItem;
  onSave: (patch: { title?: string; url?: string | null; estimatedMinutes?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.reading.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <li ref={setNodeRef} style={style} className="border border-border rounded-lg p-3 bg-card">
      <ReadingRowBody
        {...props}
        dragHandle={<DragHandle attributes={attributes} listeners={listeners} />}
      />
    </li>
  );
}

function ReadingRowBody({
  reading,
  onSave,
  onDelete,
  dragHandle,
}: {
  reading: ReadingItem;
  onSave: (patch: { title?: string; url?: string | null; estimatedMinutes?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
  dragHandle: React.ReactNode;
}) {
  const [draft, setDraft] = useState({
    title: reading.title,
    url: reading.url ?? "",
    estimatedMinutes: String(reading.estimatedMinutes),
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setDraft({
      title: reading.title,
      url: reading.url ?? "",
      estimatedMinutes: String(reading.estimatedMinutes),
    });
  }, [reading.id, reading.title, reading.url, reading.estimatedMinutes]);

  const dirty =
    draft.title !== reading.title ||
    (draft.url || null) !== (reading.url || null) ||
    Number(draft.estimatedMinutes) !== reading.estimatedMinutes;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        title: draft.title,
        url: draft.url.trim() || null,
        estimatedMinutes: Number(draft.estimatedMinutes) || 0,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="grid sm:grid-cols-[auto_1fr_1fr_90px_auto] gap-2 items-end">
      <div className="flex items-end h-9">{dragHandle}</div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          className="mt-1 h-9"
        />
      </div>
      <div>
        <Label className="text-xs">URL</Label>
        <Input
          value={draft.url}
          onChange={(e) => setDraft({ ...draft, url: e.target.value })}
          placeholder="https://…"
          className="mt-1 h-9"
        />
      </div>
      <div>
        <Label className="text-xs">Minutes</Label>
        <Input
          type="number"
          min="0"
          value={draft.estimatedMinutes}
          onChange={(e) => setDraft({ ...draft, estimatedMinutes: e.target.value })}
          className="mt-1 h-9"
        />
      </div>
      <div className="flex gap-1">
        <Button type="submit" size="sm" variant="outline" className="rounded-full h-9" disabled={!dirty || saving}>
          <Save className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-full h-9 text-destructive hover:text-destructive"
          disabled={deleting}
          onClick={async () => {
            setDeleting(true);
            try {
              await onDelete();
            } finally {
              setDeleting(false);
            }
          }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
}

function HomeworkEditor({
  lessonId,
  homework,
  onChange,
}: {
  lessonId: number;
  homework: HomeworkItem[];
  onChange: () => void;
}) {
  const create = useCreateMyLessonHomework();
  const update = useUpdateMyHomework();
  const remove = useDeleteMyHomework();
  const reorder = useReorderMyLessonHomework();
  const { toast } = useToast();
  const sensors = useDragSensors();
  const [draft, setDraft] = useState({ title: "", instructions: "", estimatedMinutes: "30" });
  const [order, setOrder] = useState<number[]>(homework.map((h) => h.id));

  useEffect(() => {
    setOrder(homework.map((h) => h.id));
  }, [homework]);

  const ordered = useMemo(() => {
    const byId = new Map(homework.map((h) => [h.id, h]));
    return order.map((id) => byId.get(id)).filter((h): h is HomeworkItem => !!h);
  }, [order, homework]);

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    const orderIndex = homework.length
      ? Math.max(...homework.map((h) => h.orderIndex)) + 1
      : 0;
    try {
      await create.mutateAsync({
        id: lessonId,
        data: {
          orderIndex,
          title: draft.title.trim(),
          instructions: draft.instructions,
          estimatedMinutes: Number(draft.estimatedMinutes) || 0,
        },
      });
      setDraft({ title: "", instructions: "", estimatedMinutes: "30" });
      onChange();
      toast({ title: "Homework added" });
    } catch (err) {
      toast({
        title: "Couldn't add homework",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIndex = order.indexOf(Number(e.active.id));
    const newIndex = order.indexOf(Number(e.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const previous = order;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    try {
      await reorder.mutateAsync({ id: lessonId, data: { ids: next } });
      onChange();
    } catch (err) {
      setOrder(previous);
      toast({
        title: "Couldn't reorder homework",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <section>
      <h3 className="font-display text-base font-semibold mb-2">Homework</h3>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ordered.map((h) => h.id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-2">
            {ordered.map((h) => (
              <SortableHomeworkRow
                key={h.id}
                item={h}
                onSave={async (patch) => {
                  try {
                    await update.mutateAsync({ id: h.id, data: patch });
                    onChange();
                    toast({ title: "Homework saved" });
                  } catch (err) {
                    toast({
                      title: "Couldn't save homework",
                      description: err instanceof Error ? err.message : "Try again.",
                      variant: "destructive",
                    });
                    throw err;
                  }
                }}
                onDelete={async () => {
                  try {
                    await remove.mutateAsync({ id: h.id });
                    onChange();
                    toast({ title: "Homework removed" });
                  } catch (err) {
                    toast({
                      title: "Couldn't remove homework",
                      description: err instanceof Error ? err.message : "Try again.",
                      variant: "destructive",
                    });
                    throw err;
                  }
                }}
              />
            ))}
            {ordered.length === 0 && (
              <li className="text-sm text-muted-foreground italic">No homework yet.</li>
            )}
          </ul>
        </SortableContext>
      </DndContext>
      <form onSubmit={onAdd} className="mt-3 space-y-2 border border-dashed border-border rounded-lg p-3">
        <div className="grid sm:grid-cols-[1fr_100px] gap-2">
          <div>
            <Label htmlFor={`new-h-title-${lessonId}`} className="text-xs">Title</Label>
            <Input
              id={`new-h-title-${lessonId}`}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Homework title"
              className="mt-1 h-9"
            />
          </div>
          <div>
            <Label htmlFor={`new-h-min-${lessonId}`} className="text-xs">Minutes</Label>
            <Input
              id={`new-h-min-${lessonId}`}
              type="number"
              min="0"
              value={draft.estimatedMinutes}
              onChange={(e) => setDraft({ ...draft, estimatedMinutes: e.target.value })}
              className="mt-1 h-9"
            />
          </div>
        </div>
        <div>
          <Label htmlFor={`new-h-inst-${lessonId}`} className="text-xs">Instructions</Label>
          <Textarea
            id={`new-h-inst-${lessonId}`}
            value={draft.instructions}
            onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
            rows={2}
            className="mt-1"
            placeholder="Describe what students should do."
          />
        </div>
        <Button type="submit" size="sm" variant="outline" className="rounded-full" disabled={create.isPending}>
          <Plus className="w-4 h-4 mr-1" /> Add homework
        </Button>
      </form>
    </section>
  );
}

function SortableHomeworkRow(props: {
  item: HomeworkItem;
  onSave: (patch: { title?: string; instructions?: string; estimatedMinutes?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <li ref={setNodeRef} style={style} className="border border-border rounded-lg p-3 bg-card">
      <HomeworkRowBody
        {...props}
        dragHandle={<DragHandle attributes={attributes} listeners={listeners} />}
      />
    </li>
  );
}

function HomeworkRowBody({
  item,
  onSave,
  onDelete,
  dragHandle,
}: {
  item: HomeworkItem;
  onSave: (patch: { title?: string; instructions?: string; estimatedMinutes?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
  dragHandle: React.ReactNode;
}) {
  const [draft, setDraft] = useState({
    title: item.title,
    instructions: item.instructions,
    estimatedMinutes: String(item.estimatedMinutes),
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setDraft({
      title: item.title,
      instructions: item.instructions,
      estimatedMinutes: String(item.estimatedMinutes),
    });
  }, [item.id, item.title, item.instructions, item.estimatedMinutes]);

  const dirty =
    draft.title !== item.title ||
    draft.instructions !== item.instructions ||
    Number(draft.estimatedMinutes) !== item.estimatedMinutes;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        title: draft.title,
        instructions: draft.instructions,
        estimatedMinutes: Number(draft.estimatedMinutes) || 0,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="grid sm:grid-cols-[auto_1fr_90px] gap-2 items-end">
        <div className="flex items-end h-9">{dragHandle}</div>
        <div>
          <Label className="text-xs">Title</Label>
          <Input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Minutes</Label>
          <Input
            type="number"
            min="0"
            value={draft.estimatedMinutes}
            onChange={(e) => setDraft({ ...draft, estimatedMinutes: e.target.value })}
            className="mt-1 h-9"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Instructions</Label>
        <Textarea
          value={draft.instructions}
          onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
          rows={2}
          className="mt-1"
        />
      </div>
      <div className="flex gap-1 justify-end">
        <Button type="submit" size="sm" variant="outline" className="rounded-full h-9" disabled={!dirty || saving}>
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-full h-9 text-destructive hover:text-destructive"
          disabled={deleting}
          onClick={async () => {
            setDeleting(true);
            try {
              await onDelete();
            } finally {
              setDeleting(false);
            }
          }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
}

export default function TeacherCourseEdit() {
  const [, params] = useRoute("/teacher/courses/:slug/edit");
  const slug = params?.slug || "";
  const { isLoaded } = useUser();
  const profileQuery = useGetMyTeacherProfile({
    query: { retry: false, queryKey: getGetMyTeacherProfileQueryKey() },
  });

  return (
    <Layout>
      <Show when="signed-out">
        <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
          <p className="text-muted-foreground mb-6">Sign in to access the teacher portal.</p>
          <Button asChild className="rounded-full">
            <Link href={`/sign-in?redirect_url=${encodeURIComponent(`${basePath}/teacher`)}`}>Sign in</Link>
          </Button>
        </div>
      </Show>
      <Show when="signed-in">
        {!isLoaded || profileQuery.isLoading ? (
          <div className="container mx-auto px-4 py-24 text-center text-muted-foreground" role="status">
            Loading…
          </div>
        ) : profileQuery.isError || !profileQuery.data ? (
          <div className="container mx-auto px-4 py-20 max-w-2xl text-center">
            <p className="text-muted-foreground mb-6">You need a teacher profile first.</p>
            <Button asChild className="rounded-full">
              <Link href="/teacher">Open teacher portal</Link>
            </Button>
          </div>
        ) : (
          <CourseEditForm slug={slug} />
        )}
      </Show>
    </Layout>
  );
}
